import { useEffect, useState } from "react";
import INVENTORY_API_BASE_URL from "../../config/api";
import Axios from "axios";
import Table from "react-bootstrap/Table";
import styles from "./goodreceived.module.css";
import Swal from "sweetalert2";
import logUserActivity from "../utils/logUserActivity";

const EMPTY_VALUE = "—";
const rowsPerPage = 10;

function displayValue(value) {
  return value === null || value === undefined || String(value).trim() === ""
    ? EMPTY_VALUE
    : String(value);
}

function formatDate(dateString) {
  if (!dateString) return EMPTY_VALUE;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE;

  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatDateInput(value) {
  if (!value) return "";
  const text = String(value);
  return text.length >= 10 ? text.slice(0, 10) : text;
}

function formatPrice(value) {
  if (value === null || value === undefined || String(value).trim() === "") {
    return EMPTY_VALUE;
  }

  const amount = Number(String(value).replace(/[₹,\s]/g, ""));
  return Number.isFinite(amount)
    ? "₹" +
        new Intl.NumberFormat("en-IN", {
          maximumFractionDigits: 2,
        }).format(amount)
    : String(value);
}

function getChallanImageUrl(image) {
  const path = String(image || "").trim();
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return "https://d3n45kopbntabe.cloudfront.net/" + path.replace(/^\/+/, "");
}

function Goodrcb() {
  const [data, setData] = useState([]);
  const [searchSKU, setSearchSKU] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editedValues, setEditedValues] = useState({});
  const [searchTitle, setSearchTitle] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadBatches = async () => {
      try {
        const response = await Axios.get(
          INVENTORY_API_BASE_URL + "/api/creategrn/getgrn",
        );
        const receivedBatches = Array.isArray(response.data) ? response.data : [];
        const sortedData = [...receivedBatches].sort(
          (first, second) =>
            new Date(second.grndate).getTime() -
            new Date(first.grndate).getTime(),
        );

        if (isMounted) {
          setData(sortedData);
          setFilteredData(sortedData);
        }
      } catch (error) {
        console.error("Unable to load received batches:", error);
        if (isMounted) {
          setLoadError("We couldn’t load the received batches. Please try again later.");
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadBatches();
    return () => {
      isMounted = false;
    };
  }, []);

  const applySearch = (value) => {
    setSearchSKU(value);
    setCurrentPage(1);

    const query = value.trim().toLowerCase();
    if (!query) {
      setFilteredData(data);
      setSearchTitle("");
      return;
    }

    const matchesSearch = (item) =>
      [item.sku, item.batchid, item.title].some((field) =>
        String(field || "").toLowerCase().includes(query),
      );

    const filtered = data.filter(matchesSearch);
    setFilteredData(filtered);

    const exactTitleMatch = data.find((item) =>
      [item.sku, item.batchid, item.title].some(
        (field) => String(field || "").trim().toLowerCase() === query,
      ),
    );
    setSearchTitle(exactTitleMatch ? exactTitleMatch.title || "" : "");
  };

  const handleSearchChange = (event) => applySearch(event.target.value);

  const handleDelete = async (id) => {
    const confirmed = await Swal.fire({
      icon: "warning",
      title: "Are you sure?",
      text: "This action will permanently delete the GRN entry.",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel",
    });

    if (!confirmed.isConfirmed) return;

    try {
      const deletedGRN = data.find((item) => item._id === id);

      await Axios.delete(INVENTORY_API_BASE_URL + "/grndelete/" + id);

      await logUserActivity("DELETE_GRN", {
        grnId: id,
        grnDetails: {
          OrderId: deletedGRN?.orderno,
          Challan: deletedGRN?.challan,
          BatchId: deletedGRN?.batchid,
          SKU: deletedGRN?.sku,
          Quantity: deletedGRN?.quantity,
          ChallanQuantity: deletedGRN?.challanquantity,
          PurchasePrice: deletedGRN?.purchasePrice,
          Timestamp: new Date().toISOString(),
        },
      });

      await Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "The item has been deleted successfully!",
      });

      setData((previous) => previous.filter((item) => item._id !== id));
      setFilteredData((previous) => previous.filter((item) => item._id !== id));
    } catch (error) {
      console.error(error);
      await Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "An error occurred while deleting the item.",
      });
    }
  };

  const handleEdit = (id) => {
    setEditingId(id);
    const rowToEdit = data.find((row) => row._id === id);
    setEditedValues(rowToEdit || {});
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setEditedValues((previous) => ({ ...previous, [name]: value }));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditedValues({});
  };

  const handleSave = async () => {
    try {
      await Axios.put(
        INVENTORY_API_BASE_URL + "/editgrn/" + editedValues._id,
        editedValues,
      );

      const original = data.find((row) => row._id === editedValues._id);
      const updatedFields = {};

      if (original) {
        Object.keys(editedValues).forEach((key) => {
          if (editedValues[key] !== original[key]) {
            updatedFields[key] = {
              old: original[key],
              new: editedValues[key],
            };
          }
        });
      }

      if (Object.keys(updatedFields).length > 0) {
        await logUserActivity("EDIT_GRN", {
          grnId: editedValues._id,
          updatedFields,
          Timestamp: new Date().toISOString(),
        });
      }

      setData((previous) =>
        previous.map((item) =>
          item._id === editedValues._id ? editedValues : item,
        ),
      );
      setFilteredData((previous) =>
        previous.map((item) =>
          item._id === editedValues._id ? editedValues : item,
        ),
      );
      setEditingId(null);
      setEditedValues({});
    } catch (error) {
      console.error(error);
      await Swal.fire({
        icon: "error",
        title: "Could not save changes",
        text: "Please try again. The GRN record has not been updated.",
      });
    }
  };

  const renderInput = (field, type, label) => {
    const value =
      type === "date"
        ? formatDateInput(editedValues[field])
        : editedValues[field] ?? "";

    return (
      <input
        className={styles.cellInput}
        name={field}
        type={type || "text"}
        value={value}
        onChange={handleInputChange}
        aria-label={"Edit " + label}
      />
    );
  };

  const totalChallanQuantity = filteredData.reduce((total, row) => {
    const value = Number.parseFloat(
      editingId === row._id
        ? editedValues.challanquantity || 0
        : row.challanquantity || 0,
    );
    return total + (Number.isFinite(value) ? value : 0);
  }, 0);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);
  const firstVisibleRow = filteredData.length ? indexOfFirstRow + 1 : 0;
  const lastVisibleRow = Math.min(indexOfLastRow, filteredData.length);

  const handleNext = () => {
    setCurrentPage((page) => Math.min(page + 1, totalPages));
  };

  const handlePrev = () => {
    setCurrentPage((page) => Math.max(page - 1, 1));
  };

  return (
    <main className={styles.goodreceived}>
      <header className={styles.pageHeader}>
        <nav className={styles.breadcrumbs} aria-label="Breadcrumb">
          <span>Inventory</span>
          <span aria-hidden="true">/</span>
          <span>Inbound</span>
          <span aria-hidden="true">/</span>
          <span aria-current="page">Manage GRN</span>
        </nav>

        <div className={styles.headingRow}>
          <div>
            <p className={styles.eyebrow}>INVENTORY / INBOUND</p>
            <h1 className={styles.goodrcvb}>Good Received Batches</h1>
            <p className={styles.pageDescription}>
              Review received quantities, purchase details, and challan documents.
            </p>
          </div>
          <a className={styles.createButton} href="/CreateGRN">
            <span aria-hidden="true">+</span>
            Create GRN
          </a>
        </div>
      </header>

      <section className={styles.controls} aria-label="Batch search and totals">
        <label className={styles.searchBox}>
          <span className={styles.searchIcon} aria-hidden="true">
            ⌕
          </span>
          <input
            type="search"
            placeholder="Search SKU, title or batch ID"
            value={searchSKU}
            onChange={handleSearchChange}
            className={styles.skusearch}
            aria-label="Search received batches"
          />
          {searchSKU && (
            <button
              className={styles.clearSearch}
              type="button"
              onClick={() => applySearch("")}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </label>

        <div className={styles.summaryGrid}>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>Batches found</span>
            <strong className={styles.summaryValue}>{filteredData.length}</strong>
          </div>
          <div className={styles.summaryCard}>
            <span className={styles.summaryLabel}>Challan received quantity</span>
            <strong className={styles.summaryValue}>
              {new Intl.NumberFormat("en-IN", {
                maximumFractionDigits: 2,
              }).format(totalChallanQuantity)}
            </strong>
          </div>
        </div>

        {searchSKU.length >= 5 && (
          <div className={styles.searchFeedback} role="status">
            <span>
              {searchTitle
                ? "Matching title: " + searchTitle
                : "No exact title match"}
            </span>
            <span>
              Total challan received quantity:{" "}
              <strong>
                {new Intl.NumberFormat("en-IN", {
                  maximumFractionDigits: 2,
                }).format(totalChallanQuantity)}
              </strong>
            </span>
          </div>
        )}
      </section>

      <section className={styles.tableCard} aria-label="Received batches">
        <div className={styles.tableHeading}>
          <div>
            <h2>Received batches</h2>
            <p>Latest received batches appear first</p>
          </div>
          <span className={styles.resultCount}>
            {filteredData.length} {filteredData.length === 1 ? "batch" : "batches"}
          </span>
        </div>

        {isLoading ? (
          <div className={styles.stateMessage} role="status">
            Loading received batches…
          </div>
        ) : loadError ? (
          <div className={styles.errorMessage} role="alert">
            {loadError}
          </div>
        ) : filteredData.length === 0 ? (
          <div className={styles.stateMessage} role="status">
            {searchSKU
              ? "No batches match this search."
              : "No received batches are available yet."}
          </div>
        ) : (
          <div className={styles.tableViewport}>
            <Table className={styles.grbtable} aria-label="Good received batches">
              <thead>
                <tr>
                  <th scope="col">Order No</th>
                  <th scope="col">Challan No</th>
                  <th scope="col">Batch ID</th>
                  <th scope="col">Received Date</th>
                  <th scope="col">SKU</th>
                  <th scope="col">Title</th>
                  <th scope="col">Ordered Qty</th>
                  <th scope="col">Challan Qty</th>
                  <th scope="col">Purchase Price</th>
                  <th scope="col">Medium</th>
                  <th scope="col">Challan Image</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              {currentRows.map((row) => {
                  const isEditing = editingId === row._id;
                  const imageUrl = getChallanImageUrl(row.challanimage);

                  return (
                    <tbody className={styles.batchGroup} key={row._id}>
                      <tr className={isEditing ? styles.editingRow : undefined}>
                        <td data-label="Order No">
                          {isEditing
                            ? renderInput("orderno", "text", "order number")
                            : displayValue(row.orderno)}
                        </td>
                        <td data-label="Challan No">
                          {isEditing
                            ? renderInput("challan", "text", "challan number")
                            : displayValue(row.challan)}
                        </td>
                        <td className={styles.batchIdCell} data-label="Batch ID">
                          {isEditing
                            ? renderInput("batchid", "text", "batch ID")
                            : displayValue(row.batchid)}
                        </td>
                        <td data-label="Received Date">
                          {isEditing
                            ? renderInput("grndate", "date", "received date")
                            : formatDate(row.grndate)}
                        </td>
                        <td className={styles.skuCell} data-label="SKU">
                          {isEditing
                            ? renderInput("sku", "text", "SKU")
                            : displayValue(row.sku)}
                        </td>
                        <td className={styles.titleCell} data-label="Title" title={displayValue(row.title)}>
                          {displayValue(row.title)}
                        </td>
                        <td data-label="Ordered Qty">
                          {isEditing
                            ? renderInput("quantity", "text", "ordered quantity")
                            : displayValue(row.quantity)}
                        </td>
                        <td data-label="Challan Qty">
                          {isEditing
                            ? renderInput(
                                "challanquantity",
                                "text",
                                "challan quantity",
                              )
                            : displayValue(row.challanquantity)}
                        </td>
                        <td data-label="Purchase Price">
                          {isEditing
                            ? renderInput(
                                "purchasePrice",
                                "text",
                                "purchase price",
                              )
                            : formatPrice(row.purchasePrice)}
                        </td>
                        <td data-label="Medium">{displayValue(row.Medium)}</td>
                        <td data-label="Challan Image">
                          {imageUrl ? (
                            <a
                              className={styles.imageLink}
                              href={imageUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              download
                            >
                              View / download
                            </a>
                          ) : (
                            <span className={styles.mutedValue}>No image</span>
                          )}
                        </td>
                        <td className={styles.actionCell} data-label="Actions">
                          <div className={styles.actionGroup}>
                            {isEditing ? (
                              <>
                                <button
                                  className={styles.saveButton}
                                  type="button"
                                  onClick={handleSave}
                                >
                                  Save
                                </button>
                                <button
                                  className={styles.cancelButton}
                                  type="button"
                                  onClick={cancelEdit}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <button
                                className={styles.editButton}
                                type="button"
                                onClick={() => handleEdit(row._id)}
                              >
                                Edit
                              </button>
                            )}
                            <button
                              className={styles.deleteButton}
                              type="button"
                              onClick={() => handleDelete(row._id)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>

                      <tr className={styles.detailRow}>
                        <td colSpan={12}>
                          <dl className={styles.detailGrid}>
                            <div className={styles.detailItem}>
                              <dt>Received quantity</dt>
                              <dd>{displayValue(row.quantityreceived)}</dd>
                            </div>
                            <div className={styles.detailItem}>
                              <dt>Good inventory</dt>
                              <dd>{displayValue(row.goodinventory)}</dd>
                            </div>
                            <div className={styles.detailItem}>
                              <dt>Bad inventory</dt>
                              <dd>{displayValue(row.badinventory)}</dd>
                            </div>
                            <div className={styles.detailItem}>
                              <dt>Short / excess</dt>
                              <dd>{displayValue(row.missexcss)}</dd>
                            </div>
                            <div className={styles.detailItem}>
                              <dt>Vehicle number</dt>
                              <dd>{displayValue(row.vehicleno)}</dd>
                            </div>
                            <div className={styles.detailItem}>
                              <dt>Transport</dt>
                              <dd>{displayValue(row.transportname)}</dd>
                            </div>
                          </dl>
                        </td>
                      </tr>
                    </tbody>
                  );
                })}
            </Table>
          </div>
        )}

        <footer className={styles.paginationcontrols}>
          <span>
            Showing {firstVisibleRow}–{lastVisibleRow} of {filteredData.length} records
          </span>
          <div className={styles.paginationActions}>
            <button
              type="button"
              onClick={handlePrev}
              disabled={currentPage <= 1}
              className={styles.pageButton}
            >
              Previous
            </button>
            <span className={styles.pageNumber}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              onClick={handleNext}
              disabled={currentPage >= totalPages || filteredData.length === 0}
              className={styles.pageButton}
            >
              Next
            </button>
          </div>
        </footer>
      </section>
    </main>
  );
}

export default Goodrcb;
